import { ChangeEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { Grid, InputValue, Stack, StackItem, withBreakpointRange } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import {
    LocalizedTypography,
    LocalizedMajorHighlight,
    WithBreakpointRange,
    isMobile,
    isMobileOrTablet,
} from '@mews-commander/ui';
import { CurrencyInput } from '@mews/b2b-ui';
import { getCurrentCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { CalculatorPanelLayout } from '../../components/Layouts/CalculatorPanelLayout';
import { AvailableFunding } from './AvailableFunding';
import { getAvailableFundingAmount } from './utils';
import { MAX_MONTHLY_SALES_AMOUNT } from './constants';

const CalculatorPanelComponent: React.FC<WithBreakpointRange> = ({ breakpointRange }) => {
    const currencyCultureInfo = useSelector(getCurrentCurrencyCultureInfo);
    const [monthlySalesAmount, setMonthlySalesAmount] = useState<InputValue>('');
    const availableFundingAmount = getAvailableFundingAmount(monthlySalesAmount);

    const handleChange = (_event: ChangeEvent<HTMLInputElement>, value: InputValue) => {
        setMonthlySalesAmount(value);
    };

    return (
        <CalculatorPanelLayout>
            <StackItem minWidth="200px" maxWidth={(isMobileOrTablet(breakpointRange) && '100%') || '300px'}>
                <LocalizedMajorHighlight textKey={TextKey.FinancialServicesCapitalFindOutHowMuchYouCouldReceive} />
            </StackItem>
            <Stack vertical spacing={(isMobile(breakpointRange) && '300') || '100'}>
                <Grid
                    templateColumns="2fr 3fr"
                    gap={(isMobileOrTablet(breakpointRange) && '300') || '1200'}
                    largePhoneMax={{ templateColumns: '1fr' }}
                >
                    <CurrencyInput
                        name="monthlySalesAmount"
                        label={<LocalizedText textKey={TextKey.FinancialServicesCapitalEnterAvarageSales} />}
                        size="extraLarge"
                        value={monthlySalesAmount}
                        onChange={handleChange}
                        decimalSeparator={currencyCultureInfo.decimalSeparator}
                        groupSeparator={currencyCultureInfo.groupSeparator}
                        allowNegative={false}
                        precision={0}
                        max={MAX_MONTHLY_SALES_AMOUNT}
                        helper={<LocalizedText textKey={TextKey.FinancialServicesCapitalMinimumMonthlySalesAmount} />}
                        data-test-id={DataTestId.CapitalCalculatorInput}
                    />
                    <AvailableFunding value={availableFundingAmount} />
                </Grid>
                <Stack>
                    {UnicodeEntity.Asterisk}
                    <LocalizedTypography
                        textStyle="bodySmall"
                        textKey={TextKey.FinancialServicesCapitalTheFiguresShownInCalculator}
                        as="div"
                    />
                </Stack>
            </Stack>
        </CalculatorPanelLayout>
    );
};

export const CalculatorPanel = withBreakpointRange(CalculatorPanelComponent);
